import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Editor from '@monaco-editor/react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../components/buttons/Button';

import * as actionTypes from '../../../../redux/actionTypes';

import {
  CreateEmailTemplateBIZnotifications,
  GetEmailTemplateBIZnotifications,
  UpdateEmailTemplateBIZnotifications
} from '../../../../redux/actions/business/notifications/EmailTemplatesActions';

import style from "../../../../global.scss";


const tagsList = [
  { value: "GENERAL", label: "GENERAL" },
  { value: "BROADCAST", label: "BROADCAST" },
  { value: "CC", label: "CC" },
  { value: "WYZIA", label: "WYZIA" },
  { value: "CHEQUE_PRINTING", label: "CHEQUE_PRINTING" },
  { value: "INVOICE_PAYMENT_REMINDERS", label: "INVOICE_PAYMENT_REMINDERS" },
  { value: "TRADE_ALLOCATIONS", label: "TRADE_ALLOCATIONS" },
];

const Data = {
  name: "",
  code: "",
  subject: "",
  template: "",
  tag: "",
  important: false
};

const EmailTemplatesModal = ({
  open,
  setOpen,
  title,
  update,
  setUpdate,
  handleGet
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.notifications"]);
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(1);
  const editorRef = useRef(null);


  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(1);
  };

  useEffect(() => {
    try {
      if(open){
        dispatch({ type: actionTypes.CLEAR_EMAIL_TEMPLATE_BIZ_NOTIFICATIONS });
        if(update.defaultMode === 1){
          // mode = 1 - create email template
          setMode(1);
        }else if(update.defaultMode === 2){
          // mode = 2 - edit email template
          dispatch(GetEmailTemplateBIZnotifications(update.id, setLoadingTemplate));
          setMode(2);
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;
    // Auto-format when editor first loads
    editor.getAction('editor.action.formatDocument').run();
  }

  useEffect(() => {
    try {
      console.log(state.emailTemplateDetails)
      setFormData({
          ...state.emailTemplateDetails
      });
    } catch (err){

    }
  }, [state]);

  // --------------- handle change ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if(mode === 1){
      // mode = 1 - create new email template
      let obj = {
        name: formData?.name.trim() || "",
        code: formData?.code?.trim().toUpperCase() || "",
        subject: formData?.subject.trim() || "",
        template: formData.template || "",
        tag: formData.tag || "",
        important: formData.important || false
      };
      dispatch(CreateEmailTemplateBIZnotifications(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else if(mode === 2){
      // mode = 2 - edit email template
      let obj = {
        name: formData?.name.trim() || "",
        code: formData?.code?.trim().toUpperCase() || "",
        subject: formData?.subject.trim() || "",
        template: formData.template || "",
        tag: formData.tag || "",
        important: formData.important || false
      };
      dispatch(UpdateEmailTemplateBIZnotifications(update?.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else{
      toast.error("Invalid modal");
      handleClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"xl"} className="modal--wide">
          {
            loadingTemplate 
            ?
            <Box height={"300px"} className="flexCenterCenterRow">
              <CircularProgress />
            </Box>
            :
            <Box width={"100%"}
              sx={{ padding: "15px"}}>
              <Typography variant='h6' sx={{ color: style["primary"] }} mb={"20px"}>
                {title}
              </Typography>
              
              <Box 
                className="flexCenter_Row"
                gap={"15px"}
                mb={"15px"}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ maxLength: 256 }}
                />
                <TextField
                  label="Code"
                  name="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={loading}
                  inputProps={{ maxLength: 10 }}
                  sx={{ minWidth: "150px" }}
                  helperText="Max 10 characters"
                />
                <FormControl fullWidth>
                  <InputLabel id="tag-select-label">Tag</InputLabel>
                  <Select
                    labelId="tag-select-label"
                    name="tag"
                    value={formData.tag}
                    label="Tag"
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {tagsList.map((tagOption) => (
                      <MenuItem key={tagOption.value} value={tagOption.value}>
                        {tagOption.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              
              </Box>

              <Box 
                className="flexCenter_Row"
                gap={"15px"}
                mb={"15px"}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ maxLength: 512 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="important"
                      checked={formData.important}
                      onChange={(e) => setFormData({ ...formData, important: e.target.checked })}
                      disabled={loading}
                    />
                  }
                  label="Important"
                  sx={{ minWidth: "150px" }}
                />
              </Box>

              <Box 
                className="flexCenter_Row"
                gap={"15px"}
                mb={"15px"}
                sx={{ alignItems: "flex-start" }}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant='subtitle2' mb={"10px"}>
                    Template (HTML)
                  </Typography>
                  {open && (
                    <Editor
                      height="500px"
                      language={"html"}
                      value={formData.template || "<!-- Enter HTML template here -->"}
                      onChange={(e) => setFormData({ ...formData, template: e })}
                      theme='vs-dark'
                      options={{
                        wordWrap: true,
                        formatOnPaste: true,
                        formatOnType: true,
                        scrollBeyondLastLine: false,
                        minimap: { enabled: false }
                      }}
                      onMount={handleEditorDidMount}
                    />
                  )}
                </Box>
                
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant='subtitle2' mb={"10px"}>
                    Email Preview
                  </Typography>
                  <Box
                    sx={{
                      height: "500px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                      overflow: "auto",
                      backgroundColor: "#ffffff"
                    }}>
                    <iframe
                      title="Email Preview"
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                          <head>
                            <meta charset="utf-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <style>
                              body {
                                margin: 0;
                                padding: 16px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                                font-size: 14px;
                                line-height: 1.6;
                                color: #000000;
                                background-color: #ffffff;
                              }
                              * {
                                box-sizing: border-box;
                              }
                            </style>
                          </head>
                          <body>
                            ${formData.template || '<p style="color: #666; font-style: italic;">Your email preview will appear here...</p>'}
                          </body>
                        </html>
                      `}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none"
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          }
          <Divider
            sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px"}}>
            <Button variant='text'
              onClick={handleClose}
              disabled={loading}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={
                !(formData?.name?.trim() !== "" && formData?.subject?.trim() !== "" && formData?.template?.trim() !== "" && formData?.tag !== "")
              }
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default EmailTemplatesModal;